// src/utils/wiStepCompletion.ts
// ─────────────────────────────────────────────────────────────────
// Phase 2: Evidence-to-Work-Instruction linkage
//
// When a field worker submits a task record in Mobile Comms, this
// module writes that completion evidence back to the corresponding
// WI step — making stage 5 of the backbone ("evidence matches
// specified work instructions") a real, computable fact.
//
// Previously: WI step completedAt/completedById sat in the static
// mock array and nothing ever wrote to them.
// Now: this localStorage store is the source of truth for step
// completions, and computeMilestoneEligibility() reads from it.
//
// Persistence: localStorage (same pattern as canonicalBOM.ts)
// ─────────────────────────────────────────────────────────────────

export interface StepCompletionRecord {
  id: string
  wiId: string                  // WorkInstruction.id
  wiReference: string           // e.g. 'WI-TP001-T1-01'
  stepNumber: number
  stepTitle: string
  isHoldPoint: boolean
  holdPointRef?: string | null
  completedById: string
  completedByName: string
  completedAt: string           // ISO timestamp
  photoCount: number
  photoUrls: string[]           // stub URLs for now
  gpsLat?: number | null
  gpsLng?: number | null
  gpsAddress?: string | null
  notes?: string | null
  mobileTaskRecordId?: string | null  // MobileTaskRecord.id that triggered this
  configuredTaskId?: string | null    // ConfiguredTask.id
  drawingTaskId?: string | null       // DrawingTask.id (pipeline trace)
  verifiedByName?: string | null      // PM/PC who accepted the mobile record
  verifiedAt?: string | null
}

export interface HoldPointClearance {
  holdPointRef: string
  wiId: string
  wiReference: string
  clearedById: string
  clearedByName: string
  clearedAt: string
  witnessName?: string | null
  witnessOrg?: string | null
  notes?: string | null
}

const COMPLETIONS_KEY  = 'tp_wi_step_completions'
const HOLD_POINTS_KEY  = 'tp_wi_hold_point_clearances'

function canStore() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'
}

function read<T>(key: string, fallback: T): T {
  if (!canStore()) return fallback
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch { return fallback }
}

function write<T>(key: string, value: T) {
  if (!canStore()) return
  window.localStorage.setItem(key, JSON.stringify(value))
}

// ── Seed data ────────────────────────────────────────────────────
// Reflects the real WI step seeding in mockDataWorkInstructions.ts
// — steps that already carry completedAt in the static data get
// matching records here so the computed eligibility is consistent.
const SEED_COMPLETIONS: StepCompletionRecord[] = [
  // WI-TP001-T1-01 — Step 1: Set out and mark foundation (COMPLETE)
  {
    id: 'sc_001', wiId: 'wi-pkg-001', wiReference: 'WI-TP001-T1-01',
    stepNumber: 1, stepTitle: 'Set out and mark foundation location', isHoldPoint: false,
    completedById: 'e1', completedByName: 'Tony Rivera',
    completedAt: '2026-06-17T08:45:00Z', photoCount: 2, photoUrls: [],
    gpsLat: -27.5526, gpsLng: 153.0522,
    gpsAddress: 'Kessels Rd & Gowan Rd intersection, NW corner',
    mobileTaskRecordId: 'mtr001', configuredTaskId: 'ct001', drawingTaskId: 'tsk003',
    verifiedByName: 'Sarah Lee', verifiedAt: '2026-06-17T16:30:00Z',
  },
  // WI-TP001-T1-01 — Step 2: Excavate (COMPLETE)
  {
    id: 'sc_002', wiId: 'wi-pkg-001', wiReference: 'WI-TP001-T1-01',
    stepNumber: 2, stepTitle: 'Excavate to 900mm, check for underground services', isHoldPoint: false,
    completedById: 'e1', completedByName: 'Tony Rivera',
    completedAt: '2026-06-17T10:15:00Z', photoCount: 3, photoUrls: [],
    gpsLat: -27.5526, gpsLng: 153.0522,
    gpsAddress: 'Kessels Rd & Gowan Rd intersection, NW corner',
    mobileTaskRecordId: 'mtr001', configuredTaskId: 'ct001', drawingTaskId: 'tsk003',
    verifiedByName: 'Sarah Lee', verifiedAt: '2026-06-17T16:30:00Z',
  },
  // WI-TP001-T1-01 — Step 3: Hold point HP-001 (CLEARED)
  {
    id: 'sc_003', wiId: 'wi-pkg-001', wiReference: 'WI-TP001-T1-01',
    stepNumber: 3, stepTitle: 'Hold point — foundation inspection before pour', isHoldPoint: true,
    holdPointRef: 'HP-001',
    completedById: 'u4', completedByName: 'Sarah Lee',
    completedAt: '2026-06-17T12:00:00Z', photoCount: 2, photoUrls: [],
    gpsLat: -27.5526, gpsLng: 153.0522, gpsAddress: null,
    mobileTaskRecordId: null, configuredTaskId: null, drawingTaskId: 'tsk003',
    verifiedByName: 'Sarah Lee', verifiedAt: '2026-06-17T12:00:00Z',
  },
]

const SEED_HOLD_POINTS: HoldPointClearance[] = [
  {
    holdPointRef: 'HP-001', wiId: 'wi-pkg-001', wiReference: 'WI-TP001-T1-01',
    clearedById: 'u4', clearedByName: 'Sarah Lee', clearedAt: '2026-06-17T12:00:00Z',
    witnessName: 'Bryce Harrington', witnessOrg: 'TMR QLD',
    notes: 'Foundation at correct depth and dimensions. Soil bearing capacity confirmed. Clear to pour.',
  },
]

let _seeded = false
function ensureSeeded() {
  if (_seeded || !canStore()) return
  _seeded = true
  if (!window.localStorage.getItem(COMPLETIONS_KEY)) write(COMPLETIONS_KEY, SEED_COMPLETIONS)
  if (!window.localStorage.getItem(HOLD_POINTS_KEY)) write(HOLD_POINTS_KEY, SEED_HOLD_POINTS)
}

// ── Public read API ───────────────────────────────────────────────

export function getAllStepCompletions(): StepCompletionRecord[] {
  ensureSeeded()
  return read<StepCompletionRecord[]>(COMPLETIONS_KEY, SEED_COMPLETIONS)
}

export function getCompletionsForWI(wiId: string): StepCompletionRecord[] {
  return getAllStepCompletions().filter(c => c.wiId === wiId)
}

export function getCompletionForStep(wiId: string, stepNumber: number): StepCompletionRecord | null {
  return getAllStepCompletions().find(c => c.wiId === wiId && c.stepNumber === stepNumber) ?? null
}

export function getAllHoldPointClearances(): HoldPointClearance[] {
  ensureSeeded()
  return read<HoldPointClearance[]>(HOLD_POINTS_KEY, SEED_HOLD_POINTS)
}

export function isHoldPointCleared(holdPointRef: string): boolean {
  return getAllHoldPointClearances().some(h => h.holdPointRef === holdPointRef)
}

export function getHoldPointClearance(holdPointRef: string): HoldPointClearance | null {
  return getAllHoldPointClearances().find(h => h.holdPointRef === holdPointRef) ?? null
}

// ── Write API ─────────────────────────────────────────────────────

/**
 * Record a field worker completing a WI step.
 * Called when a mobile task record is accepted in MobileComms.
 */
export function recordStepCompletion(
  rec: Omit<StepCompletionRecord, 'id'>
): StepCompletionRecord {
  const all = getAllStepCompletions()
  const existing = all.findIndex(c => c.wiId === rec.wiId && c.stepNumber === rec.stepNumber)
  const newRec: StepCompletionRecord = { ...rec, id: `sc_${Date.now()}` }
  if (existing >= 0) {
    all[existing] = newRec
  } else {
    all.unshift(newRec)
  }
  write(COMPLETIONS_KEY, all)
  return newRec
}

/**
 * Clear a hold point — called by PM/PC after inspection.
 */
export function clearHoldPoint(clearance: Omit<HoldPointClearance, never>): HoldPointClearance {
  const all = getAllHoldPointClearances()
  const existing = all.findIndex(h => h.holdPointRef === clearance.holdPointRef)
  if (existing >= 0) {
    all[existing] = clearance
  } else {
    all.unshift(clearance)
  }
  write(HOLD_POINTS_KEY, all)
  // Also create a step completion record for the hold point
  const all2 = getAllStepCompletions()
  const existingStep = all2.findIndex(c => c.holdPointRef === clearance.holdPointRef)
  const hpRec: StepCompletionRecord = {
    id: `sc_hp_${Date.now()}`, wiId: clearance.wiId, wiReference: clearance.wiReference,
    stepNumber: 99, stepTitle: `Hold point — ${clearance.holdPointRef}`,
    isHoldPoint: true, holdPointRef: clearance.holdPointRef,
    completedById: clearance.clearedById, completedByName: clearance.clearedByName,
    completedAt: clearance.clearedAt, photoCount: 0, photoUrls: [],
    notes: clearance.notes ?? null,
    verifiedByName: clearance.witnessName ?? null, verifiedAt: clearance.clearedAt,
  }
  if (existingStep >= 0) { all2[existingStep] = hpRec } else { all2.unshift(hpRec) }
  write(COMPLETIONS_KEY, all2)
  return clearance
}

// ── WI completion summary ─────────────────────────────────────────

export type WICompletionSummary = {
  wiId: string
  wiReference: string
  stepsTotal: number
  stepsCompleted: number
  holdPointsTotal: number
  holdPointsCleared: number
  isComplete: boolean           // all non-hold-point steps done AND all HPs cleared
  completionPct: number
  latestCompletionAt: string | null
}

export function getWICompletionSummary(
  wiId: string,
  wiReference: string,
  totalSteps: number,
  holdPointRefs: string[],
): WICompletionSummary {
  const completions = getCompletionsForWI(wiId)
  const nonHPCompletions = completions.filter(c => !c.isHoldPoint)
  const stepsCompleted = Math.min(nonHPCompletions.length, totalSteps)
  const holdPointsCleared = holdPointRefs.filter(ref => isHoldPointCleared(ref)).length
  const isComplete = stepsCompleted >= totalSteps && holdPointsCleared >= holdPointRefs.length
  const latestAt = completions.map(c => c.completedAt).sort().reverse()[0] ?? null
  return {
    wiId, wiReference,
    stepsTotal: totalSteps,
    stepsCompleted,
    holdPointsTotal: holdPointRefs.length,
    holdPointsCleared,
    isComplete,
    completionPct: totalSteps === 0 ? 100 : Math.round((stepsCompleted / totalSteps) * 100),
    latestCompletionAt: latestAt,
  }
}

// ── Evidence pack builder (feeds ProgressPaymentClaims) ───────────

export type LiveEvidenceItem = {
  id: string
  type: 'STEP_COMPLETION' | 'HOLD_POINT_CLEARANCE'
  wiId: string
  wiReference: string
  stepTitle: string
  stepNumber: number
  completedByName: string
  completedAt: string
  photoCount: number
  holdPointRef?: string | null
  gpsAddress?: string | null
  notes?: string | null
  verifiedByName?: string | null
  verifiedAt?: string | null
  included: boolean             // PM can toggle inclusion in a claim
}

/**
 * Pull all real completion evidence for a set of WI IDs or references.
 * This replaces the hand-typed EvidencePhoto[] arrays in MOCK_CLAIMS.
 */
export function pullLiveEvidence(
  linkedWIs: string[],         // WI references e.g. ['WI-TP001-T1-01', ...]
  wiIdMap: Record<string, string>,  // reference → id map
): LiveEvidenceItem[] {
  const items: LiveEvidenceItem[] = []

  linkedWIs.forEach(wiRef => {
    const wiId = wiIdMap[wiRef] ?? wiRef
    const completions = getCompletionsForWI(wiId)
    completions.forEach(c => {
      items.push({
        id: c.id,
        type: c.isHoldPoint ? 'HOLD_POINT_CLEARANCE' : 'STEP_COMPLETION',
        wiId: c.wiId, wiReference: c.wiReference,
        stepTitle: c.stepTitle, stepNumber: c.stepNumber,
        completedByName: c.completedByName, completedAt: c.completedAt,
        photoCount: c.photoCount, holdPointRef: c.holdPointRef ?? null,
        gpsAddress: c.gpsAddress ?? null, notes: c.notes ?? null,
        verifiedByName: c.verifiedByName ?? null, verifiedAt: c.verifiedAt ?? null,
        included: true,  // all included by default; PM can exclude
      })
    })
  })

  // Also include hold point clearances not covered by step completions
  getAllHoldPointClearances()
    .filter(h => linkedWIs.includes(h.wiReference))
    .forEach(h => {
      if (!items.find(i => i.holdPointRef === h.holdPointRef)) {
        items.push({
          id: `hp_${h.holdPointRef}`, type: 'HOLD_POINT_CLEARANCE',
          wiId: h.wiId, wiReference: h.wiReference,
          stepTitle: `Hold point cleared — ${h.holdPointRef}`,
          stepNumber: 0,
          completedByName: h.clearedByName, completedAt: h.clearedAt,
          photoCount: 0, holdPointRef: h.holdPointRef,
          gpsAddress: null, notes: h.notes ?? null,
          verifiedByName: h.witnessName ?? null, verifiedAt: h.clearedAt,
          included: true,
        })
      }
    })

  return items.sort((a, b) => a.completedAt.localeCompare(b.completedAt))
}
